// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { linkStyles, linkTranslatableIcon } from "@store/Themes/common/mixins/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    displayDynamicTitle = "displayDynamicTitle",
    linksWrapperWidth = "linksWrapperWidth",
    titleLink = "titleLink",
    links = "links",
    imageUrl = "imageUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.displayDynamicTitle]: boolean;
        [fields.linksWrapperWidth]: number;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: (LinkModel & {
            fields: {
                [fields.imageUrl]: string;
            };
        })[];
    };
}

export interface StyledLinkListStyles {
    wrapper?: InjectableCss;
    linkListWrapper?: InjectableCss;
    prefixTitle?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    linkImg?: ImgProps;
    linkTextArrow?: InjectableCss;
    linkWrapper?: InjectableCss;
}

export const styles: StyledLinkListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    prefixTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-right: ${pxToRem(10)};
        `,
    },
    title: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.largest};
            text-align: center;
            width: 100%;

            ${breakpoint("sm")} {
                margin-bottom: ${pxToRem(30)};
            }

            ${breakpoint("md")} {
                margin-bottom: ${pxToRem(40)};
            }

            ${breakpoint("lg")} {
                margin-bottom: ${pxToRem(60)};
            }
        `,
    },
    linksWrapper: {
        css: css`
            display: grid;
        `,
    },
    link: {
        css: css`
            justify-content: center;
            text-align: center;
            width: 100%;
            height: 100%;
            ${linkStyles("text", "text", false, false)}
            ${linkTranslatableIcon(true)}

            ${breakpoint("xs")} {
                padding: ${pxToRem(20, 0)};
            }

            ${breakpoint("sm")} {
                padding: ${pxToRem(10)};
            }

            ${breakpoint("mg")} {
                padding: ${pxToRem(15)};
            }

            ${breakpoint("lg")} {
                padding: ${pxToRem(20)};
            }

            [class^="IconWrapper"] {
                position: relative;
                left: ${pxToRem(10)};

                ${breakpoint("sm")} {
                    left: 0;
                }

                ${breakpoint("md")} {
                    left: ${pxToRem(10)};
                }
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                height: 100%;
                font-size: ${({ theme }) => theme.design.typography.size.larger};

                ${breakpoint("sm")} {
                    font-size: ${({ theme }) => theme.design.typography.size.base};
                }

                ${breakpoint("md")} {
                    font-size: ${({ theme }) => theme.design.typography.size.larger};
                }
            `,
        },
    },
    linkImg: {
        css: css`
            width: 100%;
            max-width: ${pxToRem(140)};
            margin-bottom: ${pxToRem(32)};
            height: auto;

            ${breakpoint("xs", "down")} {
                margin-bottom: ${pxToRem(10)};
            }
        `,
    },
    linkTextArrow: {
        css: css`
            display: flex;
            align-items: center;
            height: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            display: none;

            ${breakpoint("xs")} {
                border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
                border-left: none;

                &:last-child {
                    border-bottom: none;
                }

                &:has(a) {
                    display: list-item;
                }
            }

            ${breakpoint("xs", "only")} {
                border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            }

            ${breakpoint("sm")} {
                border-bottom: none;
                border-left: ${pxToRem(1)} solid ${getColor("common.border")};

                &.hasLink:not(:has(~ .hasLink)) {
                    border-right: ${pxToRem(1)} solid ${getColor("common.border")};
                }
            }
        `,
    },
};

export const StyledLinkList: FC<OwnProps> = ({ fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const userFirstName = useAppSelector(state => {
        const { firstName, userName } = state.context.session;
        return firstName || userName;
    });

    const linksWrapperStyles: InjectableCss = {
        ...styles.linksWrapper,
        css: css`
            ${styles.linksWrapper?.css}
            grid-template-columns: repeat(${links.length}, 1fr);

            ${breakpoint("xs", "down")} {
                grid-template-columns: 1fr;
            }

            ${breakpoint("md")} {
                width: ${fields.linksWrapperWidth}%;
            }
        `,
    };

    const prefixTitle = translate("Hello {name}").replace("{name}", userFirstName);

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.title ? (
                <Typography {...styles.title}>
                    {fields.displayDynamicTitle && <Typography {...styles.prefixTitle}>{prefixTitle}</Typography>}
                    {fields.title}
                </Typography>
            ) : null}
            {links?.length ? (
                <StyledWrapper as="ul" {...linksWrapperStyles}>
                    {links.map((link, index) => (
                        /* eslint-disable-next-line react/no-array-index-key */
                        <StyledWrapper
                            {...styles.linkWrapper}
                            as="li"
                            key={index}
                            className={link.url ? "hasLink" : ""}
                        >
                            {link.url ? (
                                <Link
                                    {...styles.link}
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : "_self"}
                                >
                                    {fields.links[index].fields.imageUrl ? (
                                        <Img
                                            src={fields.links[index].fields.imageUrl}
                                            altText={fields.links[index].fields.overriddenTitle || link.title}
                                            loading="lazy"
                                            {...styles.linkImg}
                                        />
                                    ) : null}
                                    <StyledWrapper {...styles.linkTextArrow}>
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                        <Icon
                                            src={ArrowForward}
                                            title={fields.links[index].fields.overriddenTitle || link.title}
                                        />
                                    </StyledWrapper>
                                </Link>
                            ) : (
                                <StyledWrapper {...styles.link}></StyledWrapper>
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: StyledLinkList,
    definition: {
        displayName: "Styled Links List",
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.displayDynamicTitle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.linksWrapperWidth,
                editorTemplate: "DropDownField",
                tooltip: "Change overall width of the links wrapper to get the wanted visual.",
                fieldType: "Contextual",
                options: [
                    {
                        displayName: "40%",
                        value: 40,
                    },
                    {
                        displayName: "50%",
                        value: 50,
                    },
                    {
                        displayName: "60%",
                        value: 60,
                    },
                    {
                        displayName: "80%",
                        value: 80,
                    },
                    {
                        displayName: "100%",
                        value: 100,
                    },
                ],
                defaultValue: 80,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                tooltip: "We recommend to use a maximum of 4 links. If you want more links, add another widget.",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        tooltip: "If no link is provided, an empty block will be displayed.",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageUrl,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
